import { motion } from "framer-motion";
import {
  Stethoscope,
  Hammer,
  UserRound,
  Building2,
  Home as HomeIcon,
  ArrowRight,
} from "lucide-react";
import SplitReveal from "@/components/anim/SplitReveal";

/**
 * Audience Sektion, beantwortet klar die Frage "Für wen arbeitet ihr?".
 * Sitzt nach dem Challenges Block (Painpoints) und vor dem Prozess.
 * Die Karten sind eng am realen Kundenstamm formuliert, keine
 * Marketingfloskeln, sondern nutzenzentrierte Zeilen.
 */

const GROUPS = [
  {
    icon: Stethoscope,
    label: "Praxen & Gesundheit",
    text: "Sichtbarkeit, die neue Patient:innen bringt.",
  },
  {
    icon: Hammer,
    label: "Handwerk & Betriebe",
    text: "Aufträge und Fachkräfte durch echte Sichtbarkeit.",
  },
  {
    icon: UserRound,
    label: "Selbstständige & Coaches",
    text: "Eine klare Marke, die Vertrauen und Anfragen schafft.",
  },
  {
    icon: Building2,
    label: "Lokaler Mittelstand",
    text: "Planbares Wachstum statt Werbung auf Verdacht.",
  },
  {
    icon: HomeIcon,
    label: "Immobilien & Gastronomie",
    text: "Bewegtbild und Content, der verkauft.",
  },
];

// 3+2 asymmetrisches Raster analog zur Challenges Sektion, visuelle
// Konsistenz zwischen den beiden Blöcken.
const COL_CLASSES = [
  "lg:col-span-2 lg:col-start-1",
  "lg:col-span-2 lg:col-start-3",
  "lg:col-span-2 lg:col-start-5",
  "lg:col-span-2 lg:col-start-2",
  "lg:col-span-2 lg:col-start-4",
];

export default function Audience() {
  return (
    <section
      id="audience"
      data-testid="audience-section"
      aria-labelledby="audience-heading"
      className="relative bg-ink py-16 lg:py-24 overflow-hidden border-t border-white/5"
    >
      {/* Ambient gradient, cyan side (wir sind die Lösung) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(700px circle at 90% 40%, rgba(63,211,219,0.07), transparent 60%)",
        }}
      />

      <div className="mx-auto max-w-[1600px] px-6 lg:px-12 relative">
        {/* Header */}
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-signal mb-6"
          >
            <span className="h-px w-10 bg-signal" />
            Zielgruppen
          </motion.div>

          <SplitReveal
            as="h2"
            id="audience-heading"
            lines={["Für wen wir arbeiten."]}
            className="font-display font-black tracking-tighter text-bone leading-[1.0]"
            duration={0.9}
            stagger={0.05}
          />
          <style>{`#audience h2 { font-size: clamp(1.75rem, 4vw, 3rem); }`}</style>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-8 text-sm lg:text-base text-bone/70 leading-relaxed max-w-3xl"
          >
            Wir arbeiten mit Unternehmen, die wissen, dass Sichtbarkeit kein
            Zufall ist, sondern ein System. Ob Praxis, Handwerksbetrieb,
            Selbstständige oder wachsender Mittelstand: Wenn du planbar
            Neukunden gewinnen und als starke Marke wahrgenommen werden
            willst, bist du bei uns richtig.
          </motion.p>
        </div>

        {/* Grid mit 5 Zielgruppen Karten */}
        <div className="mt-14 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-6">
          {GROUPS.map((g, i) => {
            const Icon = g.icon;
            return (
              <motion.div
                key={g.label}
                data-testid={`audience-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{
                  duration: 0.7,
                  delay: 0.1 + i * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -4 }}
                className={`group relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 lg:p-7 hover:border-signal/30 hover:bg-white/[0.04] transition-[background-color,border-color] duration-500 ${COL_CLASSES[i]}`}
              >
                {/* Nummer */}
                <div className="absolute top-4 right-5 text-[10px] font-mono uppercase tracking-widest text-bone/25 tabular-nums">
                  0{i + 1}
                </div>

                {/* Icon in cyan (Lösungsseite) */}
                <div
                  className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-5"
                  style={{
                    background: "rgba(63,211,219,0.08)",
                    border: "1px solid rgba(63,211,219,0.25)",
                    color: "#3FD3DB",
                  }}
                >
                  <Icon size={20} strokeWidth={1.8} />
                </div>

                <div className="text-base lg:text-lg font-display font-bold text-bone mb-2 leading-tight">
                  {g.label}
                </div>
                <p className="text-sm text-bone/60 leading-relaxed">
                  {g.text}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Abschluss Zeile mit Link */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-12 lg:mt-16 text-sm lg:text-base text-bone/70 leading-relaxed max-w-3xl"
        >
          Nicht sicher, ob du reinpasst?{" "}
          <a
            href="#contact"
            data-testid="audience-contact-link"
            className="inline-flex items-center gap-1.5 text-signal hover:text-signal/80 font-semibold underline-offset-4 hover:underline transition-colors"
          >
            Genau dafür ist das kostenlose Erstgespräch da.
            <ArrowRight size={14} className="translate-y-px" />
          </a>
        </motion.p>
      </div>
    </section>
  );
}
